import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect, useCallback } from "react";
import {
  CalendarCheck, Building2,
  ShieldCheck, DollarSign, Percent, Star,
  Clock, CheckCircle2, XCircle, Loader2,
  ArrowUpRight, ArrowDownRight,
  ChevronRight, Plus, CalendarDays, Banknote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { venuesApi, bookingsApi, managerApi, adminApi, eventsApi, refundsApi, withdrawalApi, type BookingListResponse, type ManagerDashboardResponse, type EventResponse, type DashboardStatsResponse, type VenueResponse } from "@/lib/api";

/* ── Admin Dashboard ─────────────────────────────────────────────
   BACKEND ENDPOINTS:
   GET /api/v1/admin/dashboard     → adminApi.dashboardStats() (KPIs)
   GET /api/v1/bookings            → bookingsApi.list() (all bookings)
   GET /api/v1/venues              → venuesApi.list() (all venues)
   NOTE: Calendar events below use local memory only (no backend yet).
   ─────────────────────────────────────────────────────────────── */
import { formatNaira } from "@/data/venues";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/admin/dashboard")({
  component: DashboardPage,
});

const STATUS_STYLE: Record<string, string> = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-purple-100 text-purple-700",
  paid: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

function getStatusStyle(status: string): string {
  const clean = String(status || "").toLowerCase().trim();
  const allowed = ["confirmed", "pending", "cancelled", "refunded", "paid", "failed", "approved", "rejected"];
  if (allowed.includes(clean)) {
    return STATUS_STYLE[clean];
  }
  return "bg-muted text-foreground";
}

const DONUT_COLORS = ["#7c3aed", "#f59e0b", "#10b981"];

function KpiCard({ icon: Icon, label, value, change, positive = true, sub }: {
  icon: React.ElementType; label: string; value: string; change?: string; positive?: boolean; sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
      <div className="flex items-start justify-between">
        <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
          <Icon className="size-5" />
        </div>
        {change && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${positive ? "text-green-600" : "text-red-500"}`}>
            {positive ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}{change}
          </span>
        )}
      </div>
      <div className="mt-4 font-[family-name:Inter] text-2xl font-bold whitespace-nowrap">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground/70">{sub}</div>}
    </div>
  );
}

/* ─── SUPER ADMIN DASHBOARD ─────────────────────────────────── */
function SuperAdminDashboard() {
  const { user } = useAuth();

  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [venueCount, setVenueCount] = useState<number | null>(null);
  const [commissionRate, setCommissionRate] = useState(0.10);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [pendingPayouts, setPendingPayouts] = useState<{ count: number; amount: number }>({ count: 0, amount: 0 });
  const [dashStats, setDashStats] = useState<DashboardStatsResponse | null>(null);

  useEffect(() => {
    bookingsApi.list({ pageSize: 100 })
      .then(res => setBookings(res.data))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoadingBookings(false));
    venuesApi.list({ pageSize: 1 })
      .then(res => setVenueCount(res.meta.total))
      .catch(() => toast.error("Failed to load venue count"))
      .finally(() => setLoadingVenues(false));
    adminApi.settings()
      .then(s => setCommissionRate(s.commissionRate))
      .catch(() => setCommissionRate(0.10));
    adminApi.dashboardStats()
      .then(s => setDashStats(s))
      .catch(() => toast.error("Failed to load dashboard stats"));
    eventsApi.list({ pageSize: 100 })
      .then(res => setEvents(res.data))
      .catch(() => setEvents([]));
    Promise.all([
      refundsApi.list({ status: "pending", pageSize: 100 }),
      withdrawalApi.list("pending"),
    ])
      .then(([refundsRes, withdrawals]) => {
        const count = refundsRes.data.length + withdrawals.length;
        const amount = refundsRes.data.reduce((s, r) => s + r.amount, 0) + withdrawals.reduce((s, w) => s + w.amount, 0);
        setPendingPayouts({ count, amount });
      })
      .catch(() => setPendingPayouts({ count: 0, amount: 0 }));
  }, []);

  const stats = useMemo(() => {
    const confirmed = bookings.filter(b => b.status.toLowerCase() === "confirmed");
    const pending = bookings.filter(b => b.status.toLowerCase() === "pending");
    const cancelled = bookings.filter(b => b.status.toLowerCase() === "cancelled");
    const revenue = confirmed.reduce((s, b) => s + b.totalAmount, 0);
    const commission = Math.round(revenue * commissionRate);
    return { confirmed: confirmed.length, pending: pending.length, cancelled: cancelled.length, revenue, commission };
  }, [bookings, commissionRate]);

  const loading = loadingBookings || loadingVenues;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">Super Admin · Platform Operations</p>
        <h1 className="mt-1 font-display text-xl font-bold">
          Welcome back, {user?.name?.split(" ")[0] || "Admin"}
        </h1>
        <p className="text-sm text-muted-foreground">Platform-wide overview for OyoBooking.</p>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading platform data…
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4">
          <KpiCard icon={DollarSign} label="Total Revenue" value={formatNaira(dashStats?.totalRevenue ?? stats.revenue)} sub="venue + event" />
          <KpiCard icon={Building2} label="Booking Revenue" value={formatNaira(dashStats?.bookingRevenue ?? stats.revenue)} sub="venue bookings" />
          <KpiCard icon={CalendarDays} label="Event Ticket Revenue" value={formatNaira(dashStats?.eventTicketRevenue ?? 0)} sub="ticket sales" />
          <KpiCard icon={Percent} label={`Commission (${Math.round(commissionRate * 100)}%)`} value={formatNaira(stats.commission)} />
          <KpiCard icon={CalendarCheck} label="Total Bookings"
            value={String(bookings.length)}
            sub={`${stats.confirmed} confirmed · ${stats.pending} pending · ${stats.cancelled} cancelled`} />
          <KpiCard icon={Building2} label="Registered Venues" value={venueCount !== null ? String(venueCount) : "—"} />
          <KpiCard icon={CalendarDays} label="Total Events" value={String(events.length)} sub="platform-wide" />
          <Link to="/admin/pending-payouts" className="contents">
            <KpiCard
              icon={Banknote}
              label="Pending Payouts"
              value={String(pendingPayouts.count)}
              sub={pendingPayouts.count > 0 ? `${formatNaira(pendingPayouts.amount)} to pay out` : "refunds + withdrawals"}
            />
          </Link>
        </div>
      )}

      {/* Recent Bookings */}
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base font-semibold">Recent Bookings</h2>
          <Link to="/admin/bookings" className="text-xs text-primary hover:underline flex items-center gap-1">
            View all <ChevronRight className="size-3" />
          </Link>
        </div>
        {loadingBookings ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarCheck className="mb-2 size-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No bookings yet.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 sm:hidden">
              {bookings.slice(0, 8).map(b => (
                <div key={b.id} className="rounded-2xl border border-border/60 bg-background p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{b.guestName}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.venueName}</p>
                      <p className="mt-2 text-[11px] font-mono text-muted-foreground truncate">{b.bookingRef}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1.5 text-[10px] font-semibold capitalize ${getStatusStyle(b.status)}`}>
                      {b.status}
                    </span>
                  </div>
                  <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-xl bg-muted/70 p-3">
                      <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Total</p>
                      <p className="mt-1 font-semibold font-[family-name:Inter]">{formatNaira(b.totalAmount)}</p>
                    </div>
                    <div className="rounded-xl bg-muted/70 p-3">
                      <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Check-in</p>
                      <p className="mt-1 font-semibold">{new Date(b.startDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="pb-3 pr-4">Ref</th>
                    <th className="pb-3 pr-4">Guest</th>
                    <th className="pb-3 pr-4">Venue</th>
                    <th className="pb-3 pr-4 text-right">Total</th>
                    <th className="pb-3 pr-4">Check-in</th>
                    <th className="pb-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {bookings.slice(0, 8).map(b => (
                    <tr key={b.id} className="hover:bg-accent/30 transition-colors">
                      <td className="py-2.5 pr-4 text-xs text-muted-foreground font-mono">{b.bookingRef}</td>
                      <td className="py-2.5 pr-4 font-medium">{b.guestName}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{b.venueName}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold font-[family-name:Inter]">{formatNaira(b.totalAmount)}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground text-xs">
                        {new Date(b.startDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="py-2.5">
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${getStatusStyle(b.status)}`}>
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Upcoming Events */}
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base font-semibold">Upcoming Events</h2>
          <Link to="/events" className="text-xs text-primary hover:underline flex items-center gap-1">
            View all <ChevronRight className="size-3" />
          </Link>
        </div>
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <CalendarDays className="mb-2 size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No events yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <th className="pb-3 pr-4">Ref</th>
                  <th className="pb-3 pr-4">Event</th>
                  <th className="pb-3 pr-4">Organizer</th>
                  <th className="pb-3 pr-4">Venue</th>
                  <th className="pb-3 pr-4">Date</th>
                  <th className="pb-3 pr-4 text-right">Registered</th>
                  <th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {events.slice(0, 8).map(ev => (
                  <tr key={ev.id} className="hover:bg-accent/30 transition-colors">
                    <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">{ev.eventRef}</td>
                    <td className="py-2.5 pr-4 font-medium">{ev.name}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{ev.organizer}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{ev.venueName}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground text-xs">{new Date(ev.date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold">{ev.totalRegistrations}</td>
                    <td className="py-2.5">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${getStatusStyle(ev.status)}`}>
                        {ev.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Approvals */}
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base font-semibold">Pending Approvals</h2>
          <Link to="/admin/venues" className="text-xs text-primary hover:underline flex items-center gap-1">
            View all <ChevronRight className="size-3" />
          </Link>
        </div>
        <div className="grid place-items-center gap-2 rounded-xl border border-dashed p-8 text-center">
          <ShieldCheck className="size-6 text-green-500" />
          <p className="text-sm text-muted-foreground">All listings reviewed.</p>
        </div>
      </div>
    </div>
  );
}

/* ─── VENUE MANAGER DASHBOARD ───────────────────────────────── */
function VenueManagerDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [dashboard, setDashboard] = useState<ManagerDashboardResponse | null>(null);
  const [apiBookings, setApiBookings] = useState<BookingListResponse[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { setEventsLoading(false); return; }
    eventsApi.list({ managerId: user.id, pageSize: 100 })
      .then(res => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { setBookingsLoading(false); return; }
    managerApi.dashboard(user.id)
      .then(res => {
        setDashboard(res);
        // Map ManagerBookingSummary to BookingListResponse shape for existing UI
        const mapped: BookingListResponse[] = res.recentBookings.map(b => ({
          id: b.bookingId,
          bookingRef: b.bookingRef,
          guestName: b.guestName,
          venueId: "", // not exposed in manager response but not rendered
          venueName: b.venueName,
          venueImage: "",
          venueLocation: "",
          startDate: b.startDate,
          endDate: b.endDate,
          daysCount: b.daysCount,
          totalAmount: b.totalAmount,
          pricePerNight: b.totalAmount / Math.max(1, b.daysCount),
          status: b.status,
          paymentStatus: "",
          createdAt: b.createdAt,
        }));
        setApiBookings(mapped);
      })
      .catch(() => { })
      .finally(() => setBookingsLoading(false));
  }, [user?.id]);

  const serviceChargeRate = dashboard?.commissionRate ?? 0.10;

  const stats = useMemo(() => {
    const confirmed = apiBookings.filter(b => b.status.toLowerCase() === "confirmed");
    const pending = apiBookings.filter(b => b.status.toLowerCase() === "pending");
    const cancelled = apiBookings.filter(b => b.status.toLowerCase() === "cancelled");
    const totalRevenue = dashboard?.totalRevenue ?? confirmed.reduce((s, b) => s + b.totalAmount, 0);
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = confirmed.filter(b => b.startDate.slice(0, 10) >= today).length;
    return { confirmed, pending, cancelled, totalRevenue, upcoming };
  }, [apiBookings, dashboard]);

  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...events]
      .filter(e => new Date(e.date) >= today)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 5);
  }, [events]);

  const isEmpty = apiBookings.length === 0 && events.length === 0 && !bookingsLoading && !eventsLoading;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Business Dashboard</p>
          <h1 className="mt-1 font-display text-xl font-bold">
            Welcome back, {user?.name?.split(" ")[0] || "Owner"}
          </h1>
          <p className="text-sm text-muted-foreground">Your property performance at a glance.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(user?.role === "hotel_owner" || user?.role === "hotel_event_owner") && (
            <Link to="/list-your-property">
              <Button size="sm" className="gap-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/95">
                <Building2 className="size-4" /> List Property
              </Button>
            </Link>
          )}
          <Link to="/events/create">
            <Button size="sm" className="gap-1.5 rounded-full">
              <Plus className="size-4" /> Create Event
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      {bookingsLoading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft animate-pulse">
              <div className="grid size-10 place-items-center rounded-lg bg-accent mb-4">
                <div className="size-5 bg-muted-foreground/20 rounded" />
              </div>
              <div className="h-7 w-24 bg-muted-foreground/15 rounded mb-2" />
              <div className="h-3 w-32 bg-muted-foreground/10 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          <KpiCard icon={DollarSign} label="Total Revenue" value={formatNaira(dashboard?.totalRevenue ?? 0)} sub="venue + event" />
          <KpiCard icon={Building2} label="Venue Revenue" value={formatNaira(dashboard?.venueRevenue ?? 0)} sub="bookings & extensions" />
          <KpiCard icon={CalendarDays} label="Event Revenue" value={formatNaira(dashboard?.eventRevenue ?? 0)} sub="ticket sales" />
          <KpiCard icon={DollarSign} label="30-Day Earnings" value={formatNaira(dashboard?.earnings30Days ?? 0)} sub={`${formatNaira(dashboard?.venueEarnings30Days ?? 0)} venue · ${formatNaira(dashboard?.eventEarnings30Days ?? 0)} event`} />
          <KpiCard icon={Percent} label="Service Charge" value={formatNaira(dashboard?.totalCommission ?? 0)} sub={`${Math.round((dashboard?.commissionRate ?? 0.10) * 100)}% rate`} />
          <KpiCard icon={DollarSign} label="Net Earnings" value={formatNaira(dashboard?.netEarnings ?? 0)} sub={`${formatNaira(dashboard?.venueNetEarnings ?? 0)} venue · ${formatNaira(dashboard?.eventNetEarnings ?? 0)} event`} />
          <KpiCard icon={CalendarCheck} label="Upcoming Bookings" value={String(stats.upcoming)} sub="confirmed, from today" />
          <KpiCard icon={Star} label="Total Bookings" value={String(apiBookings.length)} sub={`${dashboard?.confirmedBookings ?? 0} confirmed · ${dashboard?.pendingBookings ?? 0} pending`} />
          <KpiCard icon={CalendarDays} label="My Events" value={String(events.length)} sub={`${upcomingEvents.length} upcoming`} />
          <KpiCard icon={Building2} label="My Venues" value={String(dashboard?.totalVenues ?? 0)} />
        </div>
      )}

      {/* Empty state CTA */}
      {isEmpty && (
        <div className="animate-fade-in flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-16 text-center">
          <CalendarDays className="mb-3 size-12 text-muted-foreground/40" />
          <p className="text-lg font-semibold">No events yet</p>
          <p className="mt-1 text-sm text-muted-foreground max-w-xs">
            Create your first booking or event — it will appear here and on your calendar.
          </p>
          <Link to="/events/create">
            <Button className="mt-5 gap-1.5 rounded-full">
              <Plus className="size-4" /> Create First Event
            </Button>
          </Link>
        </div>
      )}

      {!isEmpty && (
        <>
          {/* Upcoming events + Bookings table */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Upcoming 5 events */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-base font-semibold">Upcoming</h2>
                <Link to="/admin/calendar" className="text-xs text-primary hover:underline flex items-center gap-1">
                  Calendar <ChevronRight className="size-3" />
                </Link>
              </div>
              {eventsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading events…
                </div>
              ) : upcomingEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No upcoming events.</p>
              ) : (
                <div className="space-y-2">
                  {upcomingEvents.map(ev => (
                    <div key={ev.id} className="flex items-start gap-3 rounded-xl border border-border/40 p-3">
                      <div className="flex flex-col items-center justify-center rounded-lg bg-primary/10 text-primary px-2.5 py-1.5 text-center shrink-0">
                        <span className="text-[10px] font-semibold uppercase">{new Date(ev.date).toLocaleDateString("en-NG", { month: "short" })}</span>
                        <span className="text-lg font-bold leading-none font-[family-name:Inter]">{new Date(ev.date).getDate()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-semibold">{ev.name}</p>
                          <Link to="/events/$eventId" params={{ eventId: ev.id }} search={{ edit: true }} className="text-[10px] text-primary hover:underline shrink-0 font-medium">Edit</Link>
                        </div>
                        <p className="text-xs text-muted-foreground">{ev.venueName} · <span className="font-[family-name:Inter]">{ev.totalRegistrations}</span> registered</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${getStatusStyle(ev.status)}`}>
                        {ev.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bookings table */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-base font-semibold">My Bookings</h2>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 font-[family-name:Inter]"><CheckCircle2 className="size-3.5 text-green-500" />{stats.confirmed.length}</span>
                  <span className="flex items-center gap-1 font-[family-name:Inter]"><Clock className="size-3.5 text-amber-500" />{stats.pending.length}</span>
                  <span className="flex items-center gap-1 font-[family-name:Inter]"><XCircle className="size-3.5 text-red-500" />{stats.cancelled.length}</span>
                </div>
              </div>
              {bookingsLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading bookings…
                </div>
              ) : apiBookings.length === 0 ? (
                <div className="flex flex-col items-center py-10 text-center">
                  <CalendarCheck className="mb-2 size-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No bookings yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        <th className="pb-3 pr-4">Ref</th>
                        <th className="pb-3 pr-4">Guest</th>
                        <th className="pb-3 pr-4">Check-in</th>
                        <th className="pb-3 pr-4 text-right">Amount</th>
                        <th className="pb-3 pr-4 text-right">Payout</th>
                        <th className="pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {apiBookings.map(b => (
                        <tr key={b.id} className="hover:bg-accent/30 transition-colors">
                          <td className="py-2.5 pr-4 font-mono text-xs">{b.bookingRef}</td>
                          <td className="py-2.5 pr-4 font-medium">{b.guestName}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground text-xs">{new Date(b.startDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}</td>
                          <td className="py-2.5 pr-4 text-right font-semibold font-[family-name:Inter]">{formatNaira(b.totalAmount)}</td>
                          <td className="py-2.5 pr-4 text-right text-green-600 font-semibold font-[family-name:Inter]">{formatNaira(Math.round(b.totalAmount * (1 - serviceChargeRate)))}</td>
                          <td className="py-2.5">
                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${getStatusStyle(b.status)}`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Venue Revenue Breakdown */}
          {dashboard && dashboard.venues.length > 0 && (
            <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-base font-semibold">Revenue by Venue</h2>
                <span className="text-xs text-muted-foreground">{dashboard.venues.length} properties</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <th className="pb-3 pr-4">Venue</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4 text-right">Bookings</th>
                      <th className="pb-3 pr-4 text-right">Revenue</th>
                      <th className="pb-3 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {dashboard.venues.map(v => (
                      <tr key={v.id} className="hover:bg-accent/30 transition-colors">
                        <td className="py-2.5 pr-4 font-medium">{v.name}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground text-xs capitalize">{v.type}</td>
                        <td className="py-2.5 pr-4 text-right font-[family-name:Inter]">{v.bookingCount}</td>
                        <td className="py-2.5 pr-4 text-right font-semibold font-[family-name:Inter]">{formatNaira(v.revenue)}</td>
                        <td className="py-2.5 text-right font-semibold text-green-600 font-[family-name:Inter]">{formatNaira(Math.round(v.revenue * (1 - serviceChargeRate)))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── MAIN ROUTER ────────────────────────────────────────────── */
function ModeratorDashboard() {
  const { user } = useAuth();
  const [listings, setListings] = useState<VenueResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    venuesApi.moderationList({ pageSize: 100 })
      .then(res => setListings(res.data))
      .catch(() => toast.error("Failed to load moderation queue"))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const status = (v: VenueResponse) => String(v.moderationStatus || "pending").toLowerCase();
    return {
      pending: listings.filter(v => status(v) === "pending").length,
      approved: listings.filter(v => status(v) === "approved").length,
      rejected: listings.filter(v => status(v) === "rejected").length,
    };
  }, [listings]);

  const pendingListings = listings
    .filter(v => String(v.moderationStatus || "pending").toLowerCase() === "pending")
    .slice(0, 6);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Moderation Dashboard</p>
          <h1 className="mt-1 font-display text-xl font-bold">Welcome back, {user?.name?.split(" ")[0] || "Moderator"}</h1>
          <p className="text-sm text-muted-foreground">Review listing submissions and keep public inventory clean.</p>
        </div>
        <Link to="/admin/moderator">
          <Button className="gap-1.5 rounded-full">
            <ShieldCheck className="size-4" /> Open queue
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-2xl border border-border/60 bg-card p-6 shadow-soft" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            <KpiCard icon={Clock} label="Pending Review" value={String(stats.pending)} sub="waiting for decision" />
            <KpiCard icon={CheckCircle2} label="Approved" value={String(stats.approved)} sub="cleared for public listing" />
            <KpiCard icon={XCircle} label="Rejected" value={String(stats.rejected)} sub="not shown publicly" />
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-base font-semibold">Pending submissions</h2>
              <Link to="/admin/moderator" className="text-xs text-primary hover:underline flex items-center gap-1">
                View all <ChevronRight className="size-3" />
              </Link>
            </div>
            {pendingListings.length === 0 ? (
              <div className="grid place-items-center gap-2 rounded-xl border border-dashed p-8 text-center">
                <ShieldCheck className="size-6 text-green-500" />
                <p className="text-sm text-muted-foreground">All listings reviewed.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {pendingListings.map(v => (
                  <Link
                    key={v.id}
                    to="/admin/moderator"
                    className="flex items-center justify-between gap-4 py-3 text-sm transition-colors hover:text-primary"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{v.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{v.type} · {v.location}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      Pending
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const BUSINESS_OWNER_ROLES = [
  "venue_manager", "hotel_owner", "event_centre_owner",
  "open_space_owner", "resort_owner", "hotel_event_owner",
  "apartment_owner", "conference_hall_owner",
];

function DashboardPage() {
  const { role } = useAuth();
  const isBusinessOwner = role ? BUSINESS_OWNER_ROLES.includes(role) : false;
  if (role === "moderator") return <ModeratorDashboard />;
  if (isBusinessOwner) return <VenueManagerDashboard />;
  return <SuperAdminDashboard />;
}
